<template>
  <div class="user">
    <a :href="`https://www.cofocus.one/profile/${user._id}`" target="_blank" class="linkToProfile"
      >Link to Cofocus profile</a
    >
    <button @click="rectifyAccount">{{ rectifyingInProgress ? "Rectifying..." : "Rectify account" }}</button>
    <div class="userItem">User ID: {{ user._id ? user._id : "!! Unset" }}</div>
    <div class="userItem">Display name: {{ user.displayName ? user.displayName : "!! Unset" }}</div>
    <div class="userItem">First name: {{ user.firstName ? user.firstName : "!! Unset" }}</div>
    <div class="userItem">Last name: {{ user.lastName ? user.lastName : "!! Unset" }}</div>
    <div class="userItem" v-if="allowedUserViewing">Email: {{ user.email }}</div>
    <div class="userItem bio" v-if="allowedUserViewing">
      <div class="section--title">Bio</div>
      <div v-html="user.bio"></div>
    </div>
    <div class="userItem">Session count: {{ user.sessionCount }}</div>
    <div v-if="user.lastAwardedBadgeCheckDate" class="userItem">
      Last online:
      <span :class="isTaylor ? 'taylor-last-online' : ''">{{ checkedCalendar }}</span>
    </div>
    <div v-if="!isTaylor && user.createdAt" class="userItem">Signed up date: {{ signupDate }}</div>
    <div v-if="!isTaylor" class="userItem">
      Verified status:
      <span :class="user.verificationStatus ? 'verificationTrue' : 'verificationFalse'">{{
        user.verificationStatus
      }}</span>
    </div>
    <img
      v-if="user.profileImage && user.profileImage.fileUrl"
      :src="user.profileImage.fileUrl"
      class="userImage userItem"
    />
    <!-- VERIFICATION CONTROLS START -->
    <div class="verificationControls" v-if="!user.verificationStatus && allowedUserViewing">
      <button class="manuallyVerify" v-if="!manualVerification" @click="manualVerification = true">
        Manually verify
      </button>
      <div v-else>
        <div>Are you sure?</div>
        <div class="confirmationButtons">
          <button @click="manualVerification = false">Cancel</button>
          <button @click="manuallyVerify(user._id)">Yes</button>
        </div>
      </div>
    </div>
    <!-- VERIFICATION CONTROLS END-->
    <div class="userSessions">
      <div class="session-toggle">
        <button @click="toggleSessionView('upcoming')" class="viewType">
          Upcoming
        </button>
        <button @click="toggleSessionView('current')" class="viewType">
          Current {{ getCurrentSession ? "1" : "n/a" }}
        </button>
        <button @click="toggleSessionView('past')" class="viewType">
          Past
        </button>
      </div>
      <div class="upcoming-sessions" v-if="viewType == 'upcomingView'">
        <div>
          {{ user.displayName }} has
          {{ userUpcomingSessions && userUpcomingSessions.length ? userUpcomingSessions.length : 0 }}
          upcoming sessions.
        </div>
        <div v-for="session in userUpcomingSessions" :key="session._id">
          <SessionPreview
            v-if="session"
            :session="session"
            @select="selectSession"
            :isSelected="selectedSession && selectedSession._id == session._id"
          />
        </div>
      </div>
      <div class="current-session" v-else-if="viewType == 'currentView'">
        <SessionPreview
          v-if="getCurrentSession"
          :session="getCurrentSession"
          @select="selectSession"
          :isSelected="selectedSession && selectedSession._id == getCurrentSession._id"
        />
      </div>
      <div class="past-sessions" v-else-if="viewType == 'pastView'">
        <div>
          {{ user.displayName }} has
          {{ user.sessionCount && user.sessionCount !== 0 ? user.sessionCount : 0 }}
          past sessions.
        </div>
        <div v-for="session in userPastSessions" :key="session._id">
          <PastSessionPreview
            v-if="session"
            ref="pastsession"
            :session="session"
            @select="selectPastSession"
            :isSelected="selectedPastSession && selectedPastSession._id == session._id"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SessionPreview from "./SessionPreview";
import PastSessionPreview from "./PastSessionPreview";
import { format } from "date-fns";
import { getUserPastSessions } from "../InternalHelpers";

export default {
  name: "User",
  props: {
    selectedSession: Object,
    selectedPastSession: Object,
    user: Object,
    isTaylor: Boolean,
    upcomingSessions: Array,
    allowedUserViewing: Boolean,
    rectifyingInProgress: Boolean,
  },
  components: {
    SessionPreview,
    PastSessionPreview,
  },
  data() {
    return {
      manualVerification: false,

      viewType: "upcomingView",

      userPastSessions: null,
    };
  },
  computed: {
    signupDate() {
      return format(new Date(this.user.createdAt), `MMMM d, y, HH:mm`);
    },
    checkedCalendar() {
      return format(new Date(this.user.lastAwardedBadgeCheckDate), `MMMM d, y, HH:mm`);
    },
    userUpcomingSessions() {
      const now = Date.now();
      let userSessions = [];
      for (var i = 0; i < this.upcomingSessions.length; i++) {
        let session = this.upcomingSessions[i];
        let datetime = new Date(session.dateTime).valueOf();
        if (session.UIDs[0] && session.UIDs[0] == this.user._id && datetime > now) {
          userSessions.push(session);
        } else if (session.UIDs[1] && session.UIDs[1] == this.user._id && datetime > now) {
          userSessions.push(session);
        }
      }
      return userSessions;
    },
    getCurrentSession() {
      const now = Date.now();
      let fiftyMinBeforeInMS = now - fiftyMinBeforeInMS;

      let userSessions = [];
      for (var i = 0; i < this.upcomingSessions.length; i++) {
        let session = this.upcomingSessions[i];
        if (session.UIDs[0] && session.UIDs[0] == this.user._id) {
          userSessions.push(session);
        } else if (session.UIDs[1] && session.UIDs[1] == this.user._id) {
          userSessions.push(session);
        }
      }

      let currentSession = null;
      for (var x = 0; x < userSessions.length; x++) {
        let session = userSessions[x];
        let datetime = new Date(session.dateTime).valueOf();
        if (datetime < now) {
          currentSession = session;
        }
      }

      return currentSession;
    },
  },
  methods: {
    rectifyAccount() {
      this.$emit("rectifyAccount", this.user._id);
    },
    resetState() {
      this.viewType = "upcomingView";
      this.userPastSessions = null;
    },
    selectSession(id) {
      this.$emit("select", id);
    },
    selectPastSession(session) {
      this.$emit("selectPastSession", session);
    },
    manuallyVerify(userID) {
      this.$emit("manuallyVerify", userID);
    },
    async toggleSessionView(type) {
      if (type == "upcoming") {
        this.viewType = "upcomingView";
      } else if (type == "current") {
        this.viewType = "currentView";
      } else if (type == "past") {
        this.viewType = "pastView";
        this.userPastSessions = await getUserPastSessions(this.user._id, 200);
      }
    },
  },
};
</script>

<style scoped>
.viewType {
  margin: 2px;
}
.section--title {
  font-weight: bold;
  margin-bottom: 10px;
  font-size: 18px;
}
.bio {
  box-shadow: 0 0 0 1px rgb(15 15 15 / 5%), 0 3px 6px rgb(15 15 15 / 10%), 0 9px 24px rgb(213 213 213 / 20%);
  background-color: rgb(255 255 255 / 80%);
  padding: 10px !important;
  border-radius: 16px;
  margin: 5px 0px;
}
</style>
